// src/services/tenantService.js
//
// Multi-tenant bootstrap.
// ---------------------------------------------------------------------
// Every document in the Al-Bayan Firestore has a `tenantId` field that
// isolates one madrasa's data from another. This service owns the
// lifecycle of the `tenants/{tenantId}` collection and the companion
// bootstrap helpers used by the signup flow and the admin console.
//
// Tenant document shape:
//   tenants/{tenantId} = {
//     name, location, locale, logoUrl,
//     primaryColor, createdAt, updatedAt, ownerUid
//   }
//
// Phase 2.1 (MIGRATION-PLAN.md): when REACT_APP_AUTH_BACKEND=api this
// module re-exports the REST-backed `tenantServiceV2.js`. The Firestore
// implementation below stays in place so the firebase backend keeps
// working unchanged during the transition; Phase 7 deletes it.

import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  query,
  where,
  Timestamp
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { userService, ROLES } from './userService';
import { referralService } from './referralService';
import {
  tenantService as tenantServiceV2,
  slugifyTenantName as slugifyTenantNameV2
} from './tenantServiceV2';

const TENANTS_COLLECTION = 'tenants';
const AUTH_BACKEND = process.env.REACT_APP_AUTH_BACKEND || 'firebase';

/**
 * Collapse a human mosque name into a stable id fragment.
 * "مسجد السلام" → "msjd-l-slm", "Al Noor Academy" → "al-noor-academy".
 */
const slugifyTenantNameLegacy = (name) => {
  if (!name) return '';
  const base = String(name)
    .trim()
    .toLowerCase()
    // strip Arabic diacritics
    .replace(/[\u064B-\u065F\u0670]/g, '')
    // collapse whitespace + punctuation to dash
    .replace(/[\s_]+/g, '-')
    .replace(/[^a-z0-9\u0600-\u06FF-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
  return base || 'tenant';
};

const tenantServiceFirebase = {
  async getTenant(tenantId) {
    if (!tenantId) return null;
    try {
      const snap = await getDoc(doc(db, TENANTS_COLLECTION, tenantId));
      if (!snap.exists()) return null;
      return { id: snap.id, ...snap.data() };
    } catch (error) {
      console.warn('tenantService.getTenant failed:', error.message);
      return null;
    }
  },

  /**
   * Create or merge a tenant document. Safe to call on an existing id:
   * the `createdAt` timestamp is preserved and only the supplied keys
   * are overwritten.
   */
  async upsertTenant(tenantId, data = {}) {
    if (!tenantId) throw new Error('upsertTenant: tenantId is required');
    const ref = doc(db, TENANTS_COLLECTION, tenantId);
    const existing = await getDoc(ref);
    if (existing.exists()) {
      await updateDoc(ref, {
        ...data,
        updatedAt: Timestamp.now()
      });
    } else {
      await setDoc(ref, {
        locale: 'ar',
        primaryColor: '#0B6E4F',
        logoUrl: null,
        ...data,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      });
    }
    return tenantId;
  },

  /**
   * One-shot bootstrap for a new madrasa signup:
   *   1. Create the tenant document
   *   2. Promote the signing-up user to admin of that tenant
   *   3. Return the final tenantId for the caller to store in context
   */
  async createTenantWithOwner({
    ownerUid,
    tenantName,
    location = '',
    displayName = '',
    email = null,
    phone = null,
    referredByCode = null,
  }) {
    if (!ownerUid) throw new Error('createTenantWithOwner: ownerUid required');
    if (!tenantName) throw new Error('createTenantWithOwner: tenantName required');
    // Make the id reasonably unique without extra round-trips.
    const base = slugifyTenantName(tenantName);
    const suffix = Math.random().toString(36).slice(2, 6);
    const tenantId = `${base}-${suffix}`;

    // Phase 5 §K.2 — generate a referral code this tenant can share, and
    // resolve the incoming referrer (if any) BEFORE writing the tenant
    // so both fields land atomically on the initial upsert.
    const referralCode = await referralService.pickUniqueCode();
    let referrer = null;
    if (referredByCode) {
      referrer = await referralService.findTenantByCode(referredByCode);
    }

    await tenantServiceFirebase.upsertTenant(tenantId, {
      name: tenantName,
      location,
      ownerUid,
      referralCode,
      referredByCode: referrer ? referrer.referralCode || null : null,
      referredByTenantId: referrer ? referrer.id : null,
    });

    // Publish the public `referralCodes/{CODE}` lookup row so another
    // signup flow can resolve this tenant's code without a full tenant
    // read. Non-fatal if it fails — the admin can regenerate later.
    try {
      await referralService.assignCodeToTenant(tenantId, referralCode, {
        tenantName,
      });
    } catch (_) {
      // swallow — the wizard already has the code in memory
    }

    await userService.upsertUserProfile(ownerUid, {
      role: ROLES.ADMIN,
      tenantId,
      displayName,
      email,
      phone
    });

    // Fire-and-forget: a referral row is nice-to-have, not load-bearing.
    // A failure here must not block the signup flow.
    if (referrer) {
      referralService
        .recordPending({
          referrerTenantId: referrer.id,
          referredTenantId: tenantId,
          referralCode: referrer.referralCode,
        })
        .catch(() => {});
    }

    return tenantId;
  },

  /**
   * List every user attached to this tenant. Used by the admin
   * console to show the teacher/parent roster.
   */
  async listUsersForTenant(tenantId) {
    if (!tenantId) return [];
    try {
      const q = query(
        collection(db, 'users'),
        where('tenantId', '==', tenantId)
      );
      const snap = await getDocs(q);
      return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    } catch (error) {
      console.warn('tenantService.listUsersForTenant failed:', error.message);
      return [];
    }
  },

  /**
   * Update tenant branding: name, location, logo, primary color, plus
   * the director (moudir) identity and the school's signature/stamp
   * assets used on issued certificates.
   */
  async updateBranding(
    tenantId,
    {
      name,
      location,
      logoUrl,
      primaryColor,
      moudirName,
      moudirFunction,
      signatureUrl,
      stampUrl,
    } = {}
  ) {
    if (!tenantId) throw new Error('updateBranding: tenantId required');
    const patch = { updatedAt: Timestamp.now() };
    if (name !== undefined) patch.name = name;
    if (location !== undefined) patch.location = location;
    if (logoUrl !== undefined) patch.logoUrl = logoUrl;
    if (primaryColor !== undefined) patch.primaryColor = primaryColor;
    if (moudirName !== undefined) patch.moudirName = moudirName;
    if (moudirFunction !== undefined) patch.moudirFunction = moudirFunction;
    if (signatureUrl !== undefined) patch.signatureUrl = signatureUrl;
    if (stampUrl !== undefined) patch.stampUrl = stampUrl;
    await updateDoc(doc(db, TENANTS_COLLECTION, tenantId), patch);
    return tenantId;
  }
};

// Phase 2.1 selector — switches at module load. Both impls expose the
// same API surface; consumers don't change.
export const tenantService = AUTH_BACKEND === 'api' ? tenantServiceV2 : tenantServiceFirebase;
export const slugifyTenantName = AUTH_BACKEND === 'api' ? slugifyTenantNameV2 : slugifyTenantNameLegacy;

export default tenantService;
